/**
 * MP stores event times as the church's wall clock ("2026-09-06T08:15:00",
 * no zone). Everything here treats that string as what it is and never runs it
 * through Date arithmetic that would shift it by the server's zone.
 */
export interface WallClock {
  y: number;
  m: number; // 1-12
  d: number;
  h: number;
  mi: number;
}

export function parseWall(s: string | null | undefined): WallClock | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(s);
  if (!m) return null;
  // Groups 1-3 are not optional in the pattern, so a match guarantees them.
  const [, y, mo, d, h, mi] = m as unknown as [string, string, string, string, string?, string?];
  return { y: +y, m: +mo, d: +d, h: h ? +h : 0, mi: mi ? +mi : 0 };
}

/** A UTC Date whose fields equal the wall clock, so Intl can format it with timeZone 'UTC'. */
function asUtc(w: WallClock): Date {
  return new Date(Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi));
}

const fmt = (opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });

const DAY = fmt({ weekday: 'short', month: 'short', day: 'numeric' });
const DAY_LONG = fmt({ weekday: 'long', month: 'long', day: 'numeric' });
const DAY_YEAR = fmt({ weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const MONTH = fmt({ month: 'short' });
const WEEKDAY = fmt({ weekday: 'short' });
const TIME = fmt({ hour: 'numeric', minute: '2-digit' });

/**
 * A true instant (an ISO string carrying a zone, like a registration window's
 * opens_at) in the reader's own time. Distinct from fmtDay, which formats
 * MP's zoneless wall clock and must not be handed one of these: the UTC
 * fields of 2026-10-15T00:00:00Z are a different day than the one Michigan
 * was having when it was written.
 */
const DAY_LOCAL = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

export function fmtInstantDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : DAY_LOCAL.format(d);
}

export function fmtDay(s: string | null | undefined, long = false): string {
  const w = parseWall(s);
  return w ? (long ? DAY_LONG : DAY).format(asUtc(w)) : '';
}

export function fmtDayYear(s: string | null | undefined): string {
  const w = parseWall(s);
  return w ? DAY_YEAR.format(asUtc(w)) : '';
}

export function fmtTime(s: string | null | undefined): string {
  const w = parseWall(s);
  if (!w) return '';
  return TIME.format(asUtc(w)).replace(':00', '');
}

/**
 * A shift time: MP's "HH:MM:SS" with no date on it. "17:45:00" reads "5:45pm".
 * Separate from fmtTime, which needs a whole wall-clock datetime.
 */
export function fmtClock(hms: string | null | undefined): string {
  if (!hms) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(hms.trim());
  if (!m) return '';
  const hour = Number(m[1]);
  const minute = m[2];
  const suffix = hour >= 12 ? 'pm' : 'am';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return minute === '00' ? `${twelve}${suffix}` : `${twelve}:${minute}${suffix}`;
}

export function dateParts(s: string | null | undefined): {
  month: string;
  day: string;
  weekday: string;
} {
  const w = parseWall(s);
  if (!w) return { month: '', day: '', weekday: '' };
  const d = asUtc(w);
  return { month: MONTH.format(d), day: String(w.d), weekday: WEEKDAY.format(d) };
}

export function sameDay(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.slice(0, 10) === b.slice(0, 10));
}

/** "Sun, Sep 6 · 8:15 AM to 11 AM" or a two-day range. */
export function fmtRange(start: string | null | undefined, end: string | null | undefined): string {
  if (!start) return '';
  if (!end) return `${fmtDay(start)} · ${fmtTime(start)}`;
  if (sameDay(start, end)) return `${fmtDay(start)} · ${fmtTime(start)} to ${fmtTime(end)}`;
  return `${fmtDay(start)} ${fmtTime(start)} to ${fmtDay(end)} ${fmtTime(end)}`;
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '';
  return n % 1 === 0 ? `$${n.toLocaleString('en-US')}` : `$${n.toFixed(2)}`;
}

/** Now, as the church's wall clock string, comparable to MP's values. */
export function nowWall(): string {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Detroit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '00';
  return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}:${g('second')}`;
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Strip HTML that MP descriptions sometimes carry, for meta tags and cards. */
export function plainText(html: string | null | undefined, max = 200): string {
  if (!html) return '';
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;|&rsquo;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Phone numbers as they are typed. Ten digits become "(248) 555-0134" while
 * the person is still typing; anything with a leading + or more than ten
 * digits is left exactly as entered rather than forced into a shape it is not.
 * The same rules as the custom-form widget, so a number typed on one Woodside
 * form looks like a number typed on any of them.
 */
export function formatPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (input.trim().startsWith('+') || digits.length > 10) return input;
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * "Sarah", "Sarah and Weston", "Sarah, Weston and Rosie".
 *
 * Two copies of this had drifted — one in the sheet, one in the curtain —
 * and they disagreed about the two-name case, which is the one a household
 * actually hits most.
 */
export function listNames(names: string[]): string {
  const real = names.map((n) => n.trim()).filter(Boolean);
  if (real.length <= 1) return real[0] ?? '';
  if (real.length === 2) return `${real[0]} and ${real[1]}`;
  return `${real.slice(0, -1).join(', ')} and ${real[real.length - 1]}`;
}
